'use strict';
import pluralize from 'pluralize';
import {
	filter, find, findLast, findIndex, sortBy, each, every, includes, chunk, nth, slice, tail, take, takeRight, indexOf
} from 'lodash';
import { JSONAPIData } from '../interfaces/json-api-data';
import { EntityManager } from '../services/sd-angular-jsonapi-entity-manager';
import { ResourceLinks } from '../interfaces/resource-links';
import { JSONAPIError, JSONAPIErrorBag } from './sd-angular-jsonapi-error';
import { JSONAPIResource } from './sd-angular-jsonapi-resource';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { JSON_API_ERROR_MESSAGES } from './error-messages';

export class Collection {
	resource: string;
	type: any;
	elements: any[] = [];

	private _copy: any[] = null;
	private _links: ResourceLinks = null;
	private _included: any;

	private _manager: EntityManager;

	original: JSONAPIData[];

	// TODO data should be properly typed
	constructor (data: JSONAPIData | JSONAPIData[] | any, included: any, type: any, manager: EntityManager) {
		this.setManager(manager);
		this.setIncluded(included);
		this.setType(type);
		this.setLinks(data.links);
		const parent = data; // TODO not sure if we need this, just storing the original in case
		data = data.data ? data.data : [];

		if ( !Array.isArray(data) ) {
			data = [ data ];
		}
		if ( data && Array.isArray(data) && data.length > 0 ) {
			this.resource = data[ 0 ].type;
		}
		this.setData(data);
	}

	private setManager (manager: EntityManager) {
		this._manager = manager;
	}

	private setIncluded (included: any) {
		this._included = included;
	}

	private setType (type: any) {
		this.type = type;
	}

	private setLinks (links: ResourceLinks) {
		this._links = links ? links : null;
	}

	private setData (data: any) {
		this.original = data;
		for ( const element of data ) {
			let className = element.type.charAt(0).toUpperCase() + element.type.slice(1);
			className = pluralize.singular(className);
			const elementInstance = new (this.type as any);
			elementInstance.manager = this._manager;
			elementInstance.initialize();
			elementInstance.setData(Object.assign(element.attributes, { id: element.id }));
			elementInstance.attributes = Object.keys(element.attributes);
			elementInstance._isLoaded = true;
			elementInstance.resource = element.type;
			elementInstance._links = element.links;
			for ( const name in element.relationships ) {
				if ( element.relationships.hasOwnProperty(name) ) {
					elementInstance.addRelationship(name, element.relationships[ name ].links,
					                                element.relationships[ name ].data);
					if ( element.relationships[ name ].data ) {
						elementInstance.hasLoadedRelationship(name);
						const elementData = filter(this._included, { type: name });
						each(elementData, (elData: any) => {
							elementInstance.associateRelationshipData(name, elData);
						});
					}
				}
			}
			this.elements.push(elementInstance);
		}
		this._copy = this.elements;
	}

	private zero (): this {
		this.elements = [];
		this._copy = [];
		return this;
	}

	reset (): this {
		this.elements = this._copy;
		return this;
	}

	nextPage () {
		if ( this.hasNext() ) {
			this._manager.request.sendWithUrl(this._links.next).subscribe((data: any) => {
				this.setLinks(data.links);
				data = data.data ? data.data : [];
				if ( !Array.isArray(data) ) {
					data = [ data ];
				}
				this.zero().setData(data);
			});
		}
	}

	private hasNext (): boolean {
		return !!(this._links && this._links.next);
	}

	previousPage () {
		if ( this.hasPrevious() ) {
			this._manager.request.sendWithUrl(this._links.prev).subscribe((data: any) => {
				this.setLinks(data.links);
				data = data.data ? data.data : [];
				if ( !Array.isArray(data) ) {
					data = [ data ];
				}
				this.zero().setData(data);
			});
		}
	}

	private hasPrevious (): boolean {
		return !!(this._links && this._links.prev);
	}

	firstPage () {
		if ( this.hasFirstLink() ) {
			this._manager.request.sendWithUrl(this._links.first).subscribe((data: any) => {
				this.setLinks(data.links);
				data = data.data ? data.data : [];
				if ( !Array.isArray(data) ) {
					data = [ data ];
				}
				this.zero().setData(data);
			});
		}
	}

	private hasFirstLink (): boolean {
		return !!(this._links && this._links.first);
	}

	lastPage () {
		if ( this.hasLastLink() ) {
			this._manager.request.sendWithUrl(this._links.last).subscribe((data: any) => {
				this.setLinks(data.links);
				data = data.data ? data.data : [];
				if ( !Array.isArray(data) ) {
					data = [ data ];
				}
				this.zero().setData(data);
			});
		}
	}

	private hasLastLink (): boolean {
		return !!(this._links && this._links.last);
	}

	refresh (): void {
		if ( this.hasSelfLink() ) {
			this._manager.request.sendWithUrl(this._links.self).subscribe((data: any) => {
				data = data.data ? data.data : [];
				if ( !Array.isArray(data) ) {
					data = [ data ];
				}
				this.zero().setData(data);
			});
		}
	}

	reload (): void {
		this.refresh();
	}

	hasSelfLink (): boolean {
		return !!(this._links.self);
	}

	delete (index: number | number[]): Observable<Collection | JSONAPIErrorBag | HttpErrorResponse> {
		if ( Array.isArray(index) ) {
			return new Observable((observer) => {
				for ( const i of index ) {
					this._deleteElementAt(i).subscribe(
						(success) => observer.next(success),
						(error) => observer.next(error)
					);
				}
			});
		} else {
			return this._deleteElementAt(index);
		}
	}

	private _deleteElementAt (index: number): Observable<Collection | JSONAPIErrorBag | HttpErrorResponse> {
		const resource: JSONAPIResource = this.elements[ index ];
		if ( resource ) {
			return resource.delete();
		} else {
			throw new JSONAPIError(JSON_API_ERROR_MESSAGES.NOT_A_RESOURCE + resource);
		}
	}

	deleteById (id: string | number): Observable<Collection | JSONAPIErrorBag | HttpErrorResponse> {
		id = id.toString();
		const resource: JSONAPIResource = find(this.elements, { id });
		if ( resource ) {
			return resource.delete();
		} else {
			throw new JSONAPIError(JSON_API_ERROR_MESSAGES.NOT_A_RESOURCE + resource);
		}
	}

	deleteAll (): Observable<Collection | JSONAPIErrorBag | HttpErrorResponse> {
		return new Observable((observer) => {
			for ( const resource of this.elements ) {
				resource.delete().subscribe(
					(success: any) => {
						observer.next(success);
					},
					(error: any) => {
						observer.error(error);
					}
				);
			}
		});
	}

	all (asOriginal: boolean = false) {
		if ( asOriginal ) {
			return this._copy;
		}
		return this.elements;
	}

	get (asOriginal: boolean = false) {
		if ( asOriginal ) {
			return this._copy;
		}
		return this.elements;
	}

	first () {
		if ( this.elements.length > 0 ) {
			return this.elements[ 0 ];
		} else {
			return {};
		}
	}

	last () {
		if ( this.elements.length > 0 ) {
			return this.elements[ this.elements.length - 1 ];
		} else {
			return {};
		}
	}

	filter (predicate: any) {
		this.elements = filter(this.elements, predicate);
		return this;
	}

	find (predicate: any) {
		this.elements = find(this.elements, predicate);
		return this;
	}

	findLast (predicate: any, index: number = this.elements.length - 1) {
		this.elements = findLast(this.elements, index, predicate);
		return this;
	}

	findIndex (predicate: any, fromIndex: number = 0) {
		return findIndex(this.elements, predicate, fromIndex);
	}

	sortBy (iteratees: any) {
		this.elements = sortBy(this.elements, iteratees);
		return this;
	}

	each (callback: any) {
		this.elements = each(this.elements, callback);
		return this;
	}

	forEach (callback: any) {
		this.elements = each(this.elements, callback);
		return this;
	}

	every (predicate: any) {
		return every(this.elements, predicate);
	}

	includes (value: any, fromIndex: number = 0) {
		return includes(this.elements, value, fromIndex);
	}

	chunk (size: number = 1) {
		this.elements = chunk(this.elements, size);
		return this;
	}

	indexOf (value: any, fromIndex: number = 0) {
		return indexOf(this.elements, value, fromIndex);
	}

	nth (index: number = 0) {
		return nth(this.elements, index);
	}

	at (index: number = 0) {
		return nth(this.elements, index);
	}

	atIndex (index: number = 0) {
		return nth(this.elements, index);
	}

	slice (start: number = 0, end: number = this.elements.length) {
		this.elements = slice(this.elements, start, end);
		return this;
	}

	add (element: any) {
		if ( !(element instanceof this.type) ) {
			throw new Error(JSON_API_ERROR_MESSAGES.NOT_AN_INSTANCE_OF + this.type.name);
		} else {
			this.elements.push(element);
			this._copy.push(element);
		}
		return this;
	}

	remove (element?: any, index?: number) {
		if ( !element && !index ) {
			throw new Error(JSON_API_ERROR_MESSAGES.NO_INDEX_OR_ELEMENT);
		}
		const copyIndex: number = this._copy.indexOf(element);
		if ( copyIndex >= 0 ) {
			this._copy.splice(copyIndex, 1);
		}
		const elementIndex: number = this.elements.indexOf(element);
		if ( elementIndex >= 0 ) {
			this.elements.splice(elementIndex, 1);
		}
		return this;
	}

	tail () {
		this.elements = tail(this.elements);
		return this;
	}

	take (amount: number = 1) {
		this.elements = take(this.elements, amount);
		return this;
	}

	takeRight (amount: number = 1) {
		this.elements = takeRight(this.elements, amount);
		return this;
	}
}
